import { useEffect, useMemo, useState } from 'react'
import BigNumber from 'bignumber.js'
import { kebabCase } from 'lodash'
import { useWeb3React } from '@web3-react/core'
import { Toast, toastTypes } from 'toonswap-uikit'
import { useSelector, useDispatch } from 'react-redux'
import { Team } from 'config/constants/types'
import { getWeb3NoAccount } from 'utils/web3'
import { getPoolPricesUsd, PRICE_PAIRS } from 'utils/poolPrices'
import useRefresh from 'hooks/useRefresh'
import { PairState, usePairs } from '../data/Reserves'
import {
  fetchFarmsPublicDataAsync,
  fetchVaultsPublicDataAsync,
  fetchPoolsPublicDataAsync,
  fetchPoolsUserDataAsync,
  fetchAllTimeVolumePublicDataAsync,
  push as pushToast,
  remove as removeToast,
  clear as clearToast,
  setBlock,
} from './actions'
import {
  State,
  Farm,
  Pool,
  Vault,
  Block,
  ProfileState,
  TeamsState,
  AchievementState,
  AllTimeState,
  OneDayState,
} from './types'
import { fetchProfile } from './profile'
import { fetchTeam, fetchTeams } from './teams'
import { fetchAchievements } from './achievements'
import { fetchPrices } from './prices'

const ZERO = new BigNumber(0)

export const useFetchPublicData = () => {
  const dispatch = useDispatch()
  const { slowRefresh } = useRefresh()

  useEffect(() => {
    dispatch(fetchFarmsPublicDataAsync())
    dispatch(fetchPoolsPublicDataAsync())
    dispatch(fetchVaultsPublicDataAsync())
    dispatch(fetchAllTimeVolumePublicDataAsync())
    // The 24h volume isn't fetched: it came from ArcherSwap's CoinGecko listing, and ToonSwap has no Ethereum volume source yet
  }, [dispatch, slowRefresh])

  useEffect(() => {
    const web3 = getWeb3NoAccount()
    const interval = setInterval(async () => {
      const blockNumber = await web3.eth.getBlockNumber()
      dispatch(setBlock(blockNumber))
    }, 6000)

    return () => clearInterval(interval)
  }, [dispatch])
}

// Farms

export const useFarms = (): Farm[] => {
  const farms = useSelector((state: State) => state.farms.data)
  return farms
}

export const useFarmFromPid = (pid): Farm => {
  const farm = useSelector((state: State) => state.farms.data.find((f) => f.pid === pid))
  return farm
}

export const useFarmFromSymbol = (lpSymbol: string): Farm => {
  const farm = useSelector((state: State) => state.farms.data.find((f) => f.lpSymbol === lpSymbol))
  return farm
}

export const useFarmUser = (pid) => {
  const farm = useFarmFromPid(pid)

  return {
    allowance: farm && farm.userData ? new BigNumber(farm.userData.allowance) : new BigNumber(0),
    tokenBalance: farm && farm.userData ? new BigNumber(farm.userData.tokenBalance) : new BigNumber(0),
    stakedBalance: farm && farm.userData ? new BigNumber(farm.userData.stakedBalance) : new BigNumber(0),
    earnings: farm && farm.userData ? new BigNumber(farm.userData.earnings) : new BigNumber(0),
  }
}

// Vaults

export const useValuts = (): Vault[] => {
  const vaults = useSelector((state: State) => state.vaults.data)
  return vaults
}

export const useVaultFromPid = (pid, provider): Vault => {
  const vault = useSelector((state: State) => state.vaults.data.find((f) => f.pid === pid && f.provider === provider))
  return vault
}

export const useVaultFromSymbol = (lpSymbol: string): Vault => {
  const vault = useSelector((state: State) => state.vaults.data.find((f) => f.lpSymbol === lpSymbol))
  return vault
}

export const useVaultUser = (pid, provider) => {
  const vault = useVaultFromPid(pid, provider)

  return {
    allowance: vault && vault.userData ? new BigNumber(vault.userData.allowance) : new BigNumber(0),
    tokenBalance: vault && vault.userData ? new BigNumber(vault.userData.tokenBalance) : new BigNumber(0),
    stakedBalance: vault && vault.userData ? new BigNumber(vault.userData.stakedBalance) : new BigNumber(0),
    earnings: vault && vault.userData ? new BigNumber(vault.userData.earnings) : new BigNumber(0),
  }
}

// All Time Volume

export const useAllTimeVolume = (): AllTimeState => {
  const data = useSelector((state: State) => state.allTimeVolume)
  return data
}

// All Time Volume

export const useOneDayVolume = (): OneDayState => {
  const data = useSelector((state: State) => state.oneDayVolume)
  return data
}

// Pools

export const usePools = (account): Pool[] => {
  const { fastRefresh } = useRefresh()
  const dispatch = useDispatch()
  useEffect(() => {
    if (account) {
      dispatch(fetchPoolsUserDataAsync(account))
    }
  }, [account, dispatch, fastRefresh])

  const pools = useSelector((state: State) => state.pools.data)
  return pools
}

export const usePoolFromPid = (sousId): Pool => {
  const pool = useSelector((state: State) => state.pools.data.find((p) => p.sousId === sousId))
  return pool
}

// Toasts
export const useToast = () => {
  const dispatch = useDispatch()
  const helpers = useMemo(() => {
    const push = (toast: Toast) => dispatch(pushToast(toast))

    return {
      toastError: (title: string, description?: string) => {
        return push({ id: kebabCase(title), type: toastTypes.DANGER, title, description })
      },
      toastInfo: (title: string, description?: string, action?: any) => {
        return push({ id: kebabCase(title), type: toastTypes.INFO, title, description, action })
      },
      toastSuccess: (title: string, description?: string) => {
        return push({ id: kebabCase(title), type: toastTypes.SUCCESS, title, description })
      },
      toastWarning: (title: string, description?: string) => {
        return push({ id: kebabCase(title), type: toastTypes.WARNING, title, description })
      },
      push,
      remove: (id: string) => dispatch(removeToast(id)),
      clear: () => dispatch(clearToast()),
    }
  }, [dispatch])

  return helpers
}

// Profile

export const useFetchProfile = () => {
  const { account } = useWeb3React()
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(fetchProfile(account))
  }, [account, dispatch])
}

export const useProfile = () => {
  const { isInitialized, isLoading, data, hasRegistered }: ProfileState = useSelector((state: State) => state.profile)
  return { profile: data, hasProfile: isInitialized && hasRegistered, isInitialized, isLoading }
}

// Teams

export const useTeam = (id: number) => {
  const team: Team = useSelector((state: State) => state.teams.data[id])
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(fetchTeam(id))
  }, [id, dispatch])

  return team
}

export const useTeams = () => {
  const { isInitialized, isLoading, data }: TeamsState = useSelector((state: State) => state.teams)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(fetchTeams())
  }, [dispatch])

  return { teams: data, isInitialized, isLoading }
}

// Achievements

export const useFetchAchievements = () => {
  const { account } = useWeb3React()
  const dispatch = useDispatch()

  useEffect(() => {
    if (account) {
      dispatch(fetchAchievements(account))
    }
  }, [account, dispatch])
}

export const useAchievements = () => {
  const achievements: AchievementState['data'] = useSelector((state: State) => state.achievements.data)
  return achievements
}

// Prices
export const useFetchPriceList = () => {
  const { slowRefresh } = useRefresh()
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(fetchPrices())
  }, [dispatch, slowRefresh])
}

export const useGetApiPrice = (token: string) => {
  const prices = useGetApiPrices()

  if (!prices) {
    return null
  }

  return prices[token.toLowerCase()]
}

// Block
export const useBlock = (): Block => {
  return useSelector((state: State) => state.block)
}

const defaultFetcher = (url) => fetch(url).then((res) => res.json())
export function useCoingeckoPrices(url: string, defaultValue: number, symbol, fetcher = defaultFetcher) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(defaultValue)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const temp = await fetcher(url)
        setData(temp[symbol].usd)
      } catch (ex) {
        console.error(ex)
        setError(ex)
      }
      setLoading(false)
    }

    fetchData()
  }, [url, symbol, fetcher])

  return [data, loading, error]
}

// Prices
// export const usePriceCoreUsd = (): BigNumber => {
//   const pid = 1 // TOON-SUPRA LP
//   const toonPriceUsd = usePriceBowUsd()
//   const farm = useFarmFromPid(pid)
//   if (!farm?.tokenPriceVsQuote) {
//     return ZERO
//   }

//   // tokenPriceVsQuote for TOON-SUPRA pool is SUPRA per TOON
//   const supraPerToon = new BigNumber(farm.tokenPriceVsQuote)
//   if (supraPerToon.isZero()) {
//     return ZERO
//   }

//   // SUPRA USD = TOON USD / (SUPRA per TOON)
//   return toonPriceUsd.div(supraPerToon)
// }

// USD prices read straight from the pools' reserves (utils/poolPrices). A missing or empty pool gives 0.
const usePoolPricesUsd = () => {
  const pairs = usePairs(PRICE_PAIRS)
  return useMemo(
    () => getPoolPricesUsd(pairs.map(([state, pair]) => (state === PairState.EXISTS ? pair : null))),
    [pairs],
  )
}

export const usePriceEthUsd = (): BigNumber => {
  return usePoolPricesUsd().eth
}

export const usePriceSupraUsd = (): BigNumber => {
  // from the SUPRA-USDC pool; the farm's figure is the fallback
  const poolPrice = usePoolPricesUsd().supra
  const farm = useFarmFromPid(2) // SUPRA-USDC LP
  if (poolPrice.gt(0)) return poolPrice
  return farm?.tokenPriceVsQuote ? new BigNumber(farm.tokenPriceVsQuote) : ZERO
}

export const usePriceBowUsd = (): BigNumber => {
  // TOON has no USDC pool: TOON USD = SUPRA per TOON (TOON-SUPRA) x SUPRA USD (SUPRA-USDC)
  const poolPrice = usePoolPricesUsd().toon
  const farm = useFarmFromPid(1) // TOON-SUPRA LP
  const supraPriceUsd = usePriceSupraUsd()
  if (poolPrice.gt(0)) return poolPrice
  return farm?.tokenPriceVsQuote ? new BigNumber(farm.tokenPriceVsQuote).times(supraPriceUsd) : ZERO
}

export const usePriceCoreUsd = (): BigNumber => {
  // ETH (and WETH) from the WETH-USDC pool
  return usePoolPricesUsd().eth
}

export const usePriceWbtcUsd = (): BigNumber => {
  // WBTC from the WETH-WBTC pool x the ETH price
  return usePoolPricesUsd().wbtc
}

export const usePricePawUsd = (): BigNumber => {
  // PAW from the PAW-USDC pool
  return usePoolPricesUsd().paw
}

export const usePrice4TokenUsd = (): BigNumber => {
  return ZERO
}

export const usePriceH4TokenUsd = (): BigNumber => {
  return ZERO
}

export const usePriceVitaUsd = (): BigNumber => {
  return ZERO
}

export const useGetApiPrices = () => {
  const ethPriceUsd = usePriceCoreUsd()
  const wbtcPriceUsd = usePriceWbtcUsd()
  const supraPriceUsd = usePriceSupraUsd()
  const toonPriceUsd = usePriceBowUsd()
  const pawPriceUsd = usePricePawUsd()

  return {
    usdt: new BigNumber(1),
    usdc: new BigNumber(1),
    tusdc: new BigNumber(1),
    // from the WETH-USDC and WETH-WBTC pools
    eth: ethPriceUsd,
    weth: ethPriceUsd,
    wbtc: wbtcPriceUsd,
    btc: wbtcPriceUsd,
    // Compatibility keys
    supra: supraPriceUsd,
    wsupra: supraPriceUsd,
    toon: toonPriceUsd,
    bow: toonPriceUsd,
    xtoon: toonPriceUsd,
    xbow: toonPriceUsd,
    // from the PAW-USDC pool
    paw: pawPriceUsd,
    // Keep legacy keys present to avoid runtime undefined access in older screens
    dai: new BigNumber(1),
    busd: new BigNumber(1),
    hunt: ZERO,
    '4token': ZERO,
    acore: ZERO,
    h4token: ZERO,
    vita: ZERO,
  }
}

export const usePriceHunterUsd = (): BigNumber => {
  return ZERO
}

export const usePriceAcoreUsd = (): BigNumber => {
  return ZERO
}
